import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("dsf_admin_token");
    const mToken = localStorage.getItem("dsf_manager_token");
    const cToken = localStorage.getItem("dsf_customer_token");
    const userJson = localStorage.getItem("dsf_user");

    if (userJson) {
      try {
        setUser(JSON.parse(userJson));
      } catch {
        /* ignore parse error */
      }
    }

    // Only revoke the local token on EXPLICIT 401/403 from the server.
    // Network errors, 5xx, CORS issues, or temporary slow responses must NEVER
    // clear the session — otherwise the user is silently logged out as soon as
    // they switch tabs or briefly lose connectivity.
    const isAuthError = (err) => {
      const s = err?.response?.status;
      return s === 401 || s === 403;
    };

    if (token || mToken) {
      api.get("/admin/me").then((r) => {
        const u = { ...r.data };
        setUser(u);
        localStorage.setItem("dsf_user", JSON.stringify(u));
      }).catch((err) => {
        if (isAuthError(err)) {
          localStorage.removeItem("dsf_admin_token");
          localStorage.removeItem("dsf_manager_token");
          localStorage.removeItem("dsf_user");
          setUser(null);
        }
        // else: transient — keep the session as-is.
      });
    }
    if (cToken) {
      api.get("/auth/me").catch((err) => {
        if (isAuthError(err)) {
          localStorage.removeItem("dsf_customer_token");
          localStorage.removeItem("dsf_user");
          setUser(null);
        }
      });
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    // unified endpoint that auto-detects role (admin / manager / customer)
    const r = await api.post("/auth/login", { email, password });
    const { token, user: u } = r.data;
    if (u.role === "admin") {
      localStorage.setItem("dsf_admin_token", token);
    } else if (u.role === "manager") {
      localStorage.setItem("dsf_manager_token", token);
    } else {
      localStorage.setItem("dsf_customer_token", token);
    }
    localStorage.setItem("dsf_user", JSON.stringify(u));
    setUser(u);
    return u;
  };

  const register = async (email, password, name, phone = "") => {
    const r = await api.post("/auth/register", { email, password, name, phone });
    const { token, user: u } = r.data;
    localStorage.setItem("dsf_customer_token", token);
    localStorage.setItem("dsf_user", JSON.stringify(u));
    setUser(u);
    return u;
  };

  const loginManager = async (email, password) => {
    const r = await api.post("/manager/login", { email, password });
    const { token, user: u } = r.data;
    localStorage.setItem("dsf_manager_token", token);
    localStorage.setItem("dsf_user", JSON.stringify(u));
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem("dsf_admin_token");
    localStorage.removeItem("dsf_manager_token");
    localStorage.removeItem("dsf_customer_token");
    localStorage.removeItem("dsf_user");
    setUser(null);
  };

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isStaff = isAdmin || isManager;
  const isCustomer = user?.role === "customer";
  const hasPerm = (p) => isAdmin || (user?.permissions || []).includes(p);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginManager, register, logout, isAdmin, isManager, isStaff, isCustomer, hasPerm }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
